// Digits drop into place from above, right-most first: the pace strip's
// amount (spending_pace_controller) and the budget card's figures
// (budget_carousel_controller) roll on exactly this. Every character that is
// not a digit stays a plain span the browser shapes as usual, so a currency
// symbol keeps its own spacing and bidi in any locale. Child elements are
// kept and rolled inside themselves, so a smaller decimal part
// (counter_controller's .counter-decimal) keeps its own styling.
//
// The element's first markup is remembered on it (data-roll-source) and the
// board is rebuilt from that every time, so rolling twice never rolls the
// roll.
//
// With `restore`, the board is taken down again once the last digit has
// settled and the element's own markup put back. Every roll column is a
// clipped box and a composited layer for the length of its transition; where
// the layout viewport is scaled (tablets) the two snap to device pixels
// differently, and a hairline of the digit went missing or doubled at the
// clip once the layer was dropped. The pace amount keeps its board (its dark
// theme styles the board's spans); the budget card restores.
const DIGIT = /\p{Nd}/u
const GROUPING = /[.,٫٬  ' ]/
export const ROLL_STAGGER_MS = 45
// The column's own transition (.pace-roll-col, application.css) plus a beat.
const ROLL_SETTLE_MS = 420 + 60

const restoreTimers = new WeakMap()

export function rollDigits(el, animate, { restore = false } = {}) {
  clearTimeout(restoreTimers.get(el))
  if (el.dataset.rollSource == null) el.dataset.rollSource = el.innerHTML
  const source = document.createElement("template")
  source.innerHTML = el.dataset.rollSource
  el.replaceChildren(...board(source.content, !animate))
  if (!animate) return

  const cols = [...el.querySelectorAll(".pace-roll-col")]
  void el.offsetWidth
  cols.forEach((col, i) => {
    setTimeout(() => col.classList.add("is-set"), (cols.length - 1 - i) * ROLL_STAGGER_MS)
  })
  if (!restore) return

  const settled = (cols.length - 1) * ROLL_STAGGER_MS + ROLL_SETTLE_MS
  restoreTimers.set(el, setTimeout(() => { el.innerHTML = el.dataset.rollSource }, settled))
}

// The nodes of `parent` rebuilt as a board: text becomes roll columns,
// elements are cloned around their own rebuilt children.
function board(parent, settled) {
  const children = [...parent.childNodes]
  return children.flatMap((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // A lone text node is trimmed, as the pace amount always was.
      const text = children.length === 1 ? node.textContent.trim() : node.textContent
      return nodesOf(boardHtml(text, settled))
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return []
    const clone = node.cloneNode(false)
    clone.replaceChildren(...board(node, settled))
    return [clone]
  })
}

function nodesOf(html) {
  const template = document.createElement("template")
  template.innerHTML = html
  return [...template.content.childNodes]
}

function boardHtml(text, settled) {
  return tokens(text).map(token => {
    if (token.run == null) return `<span class="pace-sep">${escape(token.sep)}</span>`
    const chars = [...token.run].map(ch => DIGIT.test(ch)
      ? `<span class="pace-roll"><span class="pace-roll-col${settled ? " is-set" : ""}"><span>${ch}</span><span>&nbsp;</span></span></span>`
      : `<span>${escape(ch)}</span>`)
    return `<span class="pace-run">${chars.join("")}</span>`
  }).join("")
}

// A run is a stretch of digits with their grouping marks; a run never ends
// in a grouping mark, so the space before a suffix symbol stays with it.
function tokens(text) {
  const out = []
  let run = ""
  let sep = ""
  const flushSep = () => { if (sep) { out.push({ sep }); sep = "" } }
  const flushRun = () => {
    if (!run) return
    const match = run.match(/^(.*\p{Nd})(.*)$/u)
    out.push({ run: match[1] })
    sep += match[2]
    run = ""
  }

  for (const ch of text) {
    if (DIGIT.test(ch)) { flushSep(); run += ch } else if (run && GROUPING.test(ch)) { run += ch } else { flushRun(); sep += ch }
  }
  flushRun()
  flushSep()
  return out
}

function escape(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}
